import type { RouteLocationRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import {
  checkLoggedIn,
  checkAdmin,
  checkDrawerNavigation,
  checkDataFetched,
} from "@/router/guard";
import { sendGeminiAppRoute, storeNewRoute } from "@/router/after";
import DocumentationRoutes from "./DocumentationRoutes";
import { RouterView } from "vue-router";
import { checkFlag } from "@/flags";

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    component: () => import("@/views/Home/HomeView.vue"),
  },
  {
    // Admin paths which require being admin to visit.
    path: "/admin",
    component: RouterView,
    beforeEnter: [checkAdmin],
    children: [
      {
        // Display the billing view.
        path: "billing",
        name: "Billing",
        component: () => import("@/views/Billing/BillingView.vue"),
      },
      {
        // Display the team/administration view.
        path: "team",
        name: "Team",
        component: () =>
          import("@/views/Administration/AdministrationView.vue"),
      },
      {
        // Display the usage view.
        path: "usage",
        name: "Usage",
        component: () => import("@/views/Usage/UsageView.vue"),
      },
      {
        path: "notifications",
        name: "Notification Channels",
        component: () => import("@/views/Notifications/NotificationsView.vue"),
      },
      {
        path: "notifications/create",
        name: "Create Channel",
        component: () =>
          import("@/views/Notifications/CreateChannel/CreateChannel.vue"),
      },
      {
        path: "notifications/edit/:channelId",
        name: "Edit Channel",
        component: () =>
          import("@/views/Notifications/CreateChannel/CreateChannel.vue"),
      },
      {
        path: "integrations",
        name: "Integrations",
        component: () => import("@/views/Integrations/IntegrationsView.vue"),
      },
      {
        path: "integrations/create",
        name: "Create Integration",
        component: () =>
          import(
            "@/views/Integrations/ManageIntegration/ManageIntegration.vue"
          ),
      },
      {
        path: "integrations/edit/:integrationId",
        name: "Edit Integration",
        component: () =>
          import(
            "@/views/Integrations/ManageIntegration/ManageIntegration.vue"
          ),
      },
    ],
  },
  {
    // Displays a full trace view, including all spans for the trace specified by the trace_id
    // and date-hour-level partition information for the specified trace.
    path: "/trace/:traceId",
    name: "trace",
    component: () => import("@/views/Trace/TraceView.vue"),
  },
  {
    // Display the preferences view
    path: "/preferences",
    name: "Preferences",
    component: () => import("@/views/Preferences/PreferencesView.vue"),
  },
  {
    // Displays service groups manager view, used for managing service groups.
    path: "/servicegroups",
    name: "Service Groups",
    component: () =>
      import("@/views/Administration/ServiceGroupManagement.vue"),
  },
  {
    // Switches from the existing service group, to the one specified.
    path: "/servicegroups/switch/:serviceGroupId",
    name: "SwitchServiceGroup",
    component: () =>
      import("@/views/SwitchServiceGroup/SwitchServiceGroup.vue"),
    beforeEnter: (to, from): boolean | RouteLocationRaw => {
      // Sets a query param that allows the Switch SG view to redirect to
      // the Checking for Data view once it resolves all the Promises
      if ((from.name as string) === "Checking for Data")
        to.query = { fromCheckingData: "true" };
      return true;
    },
  },
  {
    // Displays service groups manager view, used for managing service groups.
    path: "/detail/:serviceGroupId",
    name: "Service Group Detail",
    component: () => import("@/views/Administration/ServiceGroupDetail.vue"),
  },
  {
    // Displays service group usage.
    path: "/usage/:serviceGroupId",
    name: "Service Group Usage",
    component: () => import("@/views/Usage/UsageView.vue"),
  },
  {
    // Display general view of traces based on the dateHour params.
    path: "/traces",
    name: "Traces",
    component: () => import("@/views/Traces/TracesView.vue"),
  },
  {
    // Display the metrics view.
    path: "/metrics",
    name: "Metrics",
    component: () => import("@/views/Metrics/MetricsView.vue"),
  },
  //Display Signup view
  {
    path: "/signup/:token?",
    name: "Sign Up",
    component: () => import("@/views/Signup/SignupView.vue"),
  },
  // Display Signup Complete view
  {
    path: "/signup/complete",
    name: "Signup Complete",
    component: () => import("@/views/Signup/SignupComplete.vue"),
  },
  //Display the "verifying Email" view
  {
    path: "/signup/verify/:email/:token",
    name: "Verifying Email",
    component: () => import("@/views/Signup/ConfirmVerify/ConfirmVerify.vue"),
  },
  //Display the signup to account view
  {
    path: "/signup/:accountId/:email/:token",
    name: "Join a Team",
    component: () => import("@/views/Signup/JoinTeam/JoinTeam.vue"),
  },
  {
    // Display the CollectPaymentInfo view.
    path: "/signup/collect-payment",
    name: "Collect Payment Info",
    component: () => import("@/views/Billing/CollectPaymentInfo.vue"),
  },
  {
    // Display the next steps view.
    path: "/signup/next-steps",
    name: "Display next steps for signup.",
    component: () => import("@/views/Signup/NextSteps.vue"),
  },
  // Display the settings view
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/views/Settings/SettingsView.vue"),
  },
  // Display the alerting view
  {
    path: "/alerting",
    name: "Alerting",
    component: () => import("@/views/Alerting/AlertingView.vue"),
  },
  {
    path: "/alerting/create",
    name: "Create Alert",
    component: () => import("@/views/Alerting/CreateAlert/CreateAlertView.vue"),
  },
  {
    path: "/alert/:alertId",
    name: "AlertDetail",
    component: () => import("@/views/Alerting/AlertDetail/AlertDetailView.vue"),
  },
  {
    // Displays a filtered spans view, based on the filters selected on the Detailed Traces view
    path: "/spans",
    name: "Spans",
    component: () => import("@/views/Spans/SpansView.vue"),
  },
  {
    path: "/login:redirect(.*)",
    name: "Login",
    component: () => import("@/views/Login/LoginView.vue"),
  },
  {
    // Display forgot password view
    path: "/login/forgot-password",
    name: "Forgot Password",
    component: () => import("@/views/Login/ForgotPassword/ForgotPassword.vue"),
  },
  {
    // Display reset password view
    path: "/login/reset-password",
    name: "Reset Password",
    component: () => import("@/views/Login/ResetPassword/ResetPassword.vue"),
  },
  {
    path: "/login/deactivated/:accountId",
    name: "Deactivated Account",
    component: () => import("@/views/Login/Deactivated/DeactivatedView.vue"),
  },
  {
    // Display the sql querier view
    path: "/querier",
    name: "Querier",
    component: () => import("@/views/Querier/QuerierView.vue"),
  },
  {
    // Display the logs view.
    path: "/logs",
    name: "Logs",
    component: () =>
      checkFlag("flag_logs_v2")
        ? import("@/views/Logs/LogsView.vue")
        : import("@/views/Logs/LogsViewV2.vue"),
  },
  {
    // Display the ChangeEmail view.
    path: "/change-email",
    name: "Change Email",
    component: () => import("@/views/ChangeEmail/ChangeEmail.vue"),
  },
  {
    // Display the Contact Us view.
    path: "/contact-us",
    name: "Contact Us",
    component: () => import("@/views/ContactUs/ContactUs.vue"),
  },
  {
    // Display the Contact Us view.
    path: "/checking-for-data",
    name: "Checking for Data",
    component: () => import("@/views/CheckingForData/CheckingForData.vue"),
  },
  DocumentationRoutes,
  {
    // This is required to handle unknown routes rerouting, which is necessary when static
    // hosting a SPA through AWS CloudFront while using history mode in the vue router.
    path: "/:catchAll(.*)",
    component: () => import("@/views/NotFound.vue"),
    name: "NotFound",
  },
];

// Add route to trigger some errors only in devel
if (process.env.VUE_APP_BUILD_MODE === "devel") {
  /* istanbul ignore next */
  routes.push({
    path: "/kaboom",
    name: "Kaboom",
    component: () => import("@/views/KaboomView.vue"),
  });
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(checkLoggedIn);
router.beforeEach(checkDataFetched);
router.beforeResolve(checkDrawerNavigation);
router.afterEach(storeNewRoute);
router.afterEach(sendGeminiAppRoute);

export default router;
